import React, { useState } from "react";
import { PlayCircle, Check } from "lucide-react";
import cityPageContent from "../data/city-page-content";
import CityOfficeCard from "./city-office-card";
import CityCtaPair from "./city-cta-pair";

const baseBullets = [
  {
    title: "Straightforward Pricing",
    body: "No hidden fees, no surprise costs — just clear and honest estimates.",
  },
  {
    title: "Fully Licensed & Insured",
    body:
      "Experienced professionals you can rely on, backed by years of local expertise.",
  },
  {
    title: "Premium Materials",
    body:
      "We use only the best, from CertainTeed roofing to Pella & Polaris windows and doors.",
  },
  {
    title: "Storm Damage & Insurance Help",
    body:
      "We handle inspections, paperwork, and insurance claims so you don't have to worry.",
  },
];

// Both the Wyoming and Jackson designs give "In Business" as 20+ Years, and the
// office card on the same page already says "Years in business: 20+ years", so
// this is a company-wide value rather than a per-city override.
const stats = [
  { label: "Leak Warranty", value: "25 Years" },
  { label: "Roofs Replaced", value: "5K+" },
  { label: "In Business", value: "20+ Years" },
];

const CityTrustedChoice = ({ city }) => {
  const {
    trustedChoiceHeading,
    trustedChoiceBody,
    trustedChoiceBullets,
  } = cityPageContent.forCity(city);
  const [videoPlaying, setVideoPlaying] = useState(false);
  // Cities that define their own bullets replace the shared list. They arrive as
  // "Lead-in — detail" strings, so split the lead-in back out to keep the
  // existing <strong>lead</strong> — detail markup.
  const bullets = trustedChoiceBullets
    ? trustedChoiceBullets.map((text) => {
        const i = text.indexOf(" — ");
        return i === -1
          ? { title: text, body: "" }
          : { title: text.slice(0, i), body: text.slice(i + 3) };
      })
    : [
        ...baseBullets,
        {
          title: `${city}'s Trusted Choice`,
          body:
            "A reputation built on delivering consistent, five-star service to every client.",
        },
      ];

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-12 md:mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-neutral-900 mb-5 leading-tight">
              {trustedChoiceHeading || `The Trusted Choice for ${city} Homeowners`}
            </h2>
            <p className="text-neutral-700 leading-relaxed mb-8 text-base md:text-lg">
              {trustedChoiceBody || (
                <>
                  Your home deserves a completely honest contractor who provides
                  quality work and is reliable. That is why so many homeowners
                  throughout {city} choose Roach Home.
                </>
              )}
            </p>
            <ul className="space-y-4 list-none p-0 m-0">
              {bullets.map((b) => (
                <li key={b.title} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-50 flex items-center justify-center mt-0.5">
                    <Check size={14} className="text-brand-500" strokeWidth={3} />
                  </div>
                  <div className="text-neutral-700 leading-relaxed">
                    <strong className="text-neutral-900 font-semibold">
                      {b.title}
                    </strong>
                    {b.body ? <> — {b.body}</> : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-8">
            {/* The design fills this slot with the Shaun Roach video still
                (Figma "image 37"); click to play, same video the homepage uses. */}
            {/* 56.25% = 16:9. Set inline rather than with `aspect-video`: that
                class is never emitted into output.css by this build, so it
                silently collapses a box whose children are absolutely placed. */}
            <div
              className="relative rounded-2xl overflow-hidden bg-neutral-900 shadow-lg w-full"
              style={{ paddingTop: "56.25%" }}
            >
              {videoPlaying ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/NZA-mxnVO0E?autoplay=1&rel=0&modestbranding=1"
                  title="Shaun Roach introduces Roach Home Improvement"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setVideoPlaying(true)}
                  className="absolute inset-0 w-full h-full p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center"
                  aria-label="Play video: Shaun Roach introduces Roach Home Improvement"
                >
                  <img
                    src="/assets/roach-homepage/shaun-roach-video-poster-1032.webp"
                    alt="Shaun Roach beside a Roach Home Improvement truck"
                    loading="lazy"
                    width="1032"
                    height="526"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <PlayCircle
                    size={80}
                    className="text-white relative z-10 drop-shadow-lg"
                    strokeWidth={1.25}
                  />
                </button>
              )}
            </div>
            {/* Office facts card sits under the media in the Wyoming design
                (Figma 439:1477); absent for cities with no officeCard data. */}
            <CityOfficeCard city={city} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 sm:gap-6 pt-10 border-t border-neutral-200">
          {stats.map((s) => (
            <div key={s.label} className="text-center sm:text-left">
              <div className="text-xs sm:text-sm uppercase tracking-wider text-neutral-500 mb-2 font-medium">
                {s.label}
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-display font-extrabold text-neutral-900 leading-none">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {trustedChoiceHeading ? <CityCtaPair city={city} className="mt-10" /> : null}
      </div>
    </section>
  );
};

export default CityTrustedChoice;
